import { LANDSTAR_PERSONALIZATION_YEARS } from "./constants.js";

export const LANDSTAR_PHONE_VALIDATION_MESSAGE =
  "Enter a valid phone number using numbers and these characters only: + - ( ) . /";

export function normalizeDriverCode(value) {
  return value.replace(/\D/g, "").slice(0, 6);
}

export function normalizeLandstarEmail(value) {
  return String(value || "").trim().toUpperCase();
}

export function isValidLandstarPhone(value) {
  const normalizedPhone = String(value || "").trim();

  return /\d/.test(normalizedPhone) && /^[0-9+\-()./ ]+$/.test(normalizedPhone);
}

export function getLandstarSelectionLimit(driverProfile) {
  const selectionRule = getLandstarSelectionRule(driverProfile);

  if (Number.isFinite(selectionRule.maxSelections)) {
    return selectionRule.maxSelections;
  }

  return Infinity;
}

export function getLandstarSelectionRule(driverProfile) {
  const normalizedYears = normalizeLandstarQualifiedYears(
    driverProfile?.qualifiedYears || driverProfile?.tierYear
  );

  if (driverProfile?.selectionRule?.mode) {
    return driverProfile.selectionRule;
  }

  if (normalizedYears === "1") {
    return {
      mode: "choose_one",
      maxSelections: 1,
      allowedSkuFamilies: ["LS-001D", "LS-001E"],
      message: "Choose either the regular or tall 1-year sweatshirt, but not both."
    };
  }

  if (normalizedYears === "18") {
    return {
      mode: "required_kit",
      maxSelections: 2,
      allowedSkuFamilies: ["LS-018D", "LS-018E"],
      requiredSkuFamilies: ["LS-018D", "LS-018E"],
      excludedSkuFamilies: ["LS-018A"],
      message: "The 18-year reward requires one jacket and one pair of pants."
    };
  }

  if (["5", "11", "15"].includes(normalizedYears)) {
    return {
      mode: "choose_one",
      maxSelections: 1
    };
  }

  return {
    mode: "choose_multiple"
  };
}

export function getLandstarProductFamilySku(productOrSku) {
  const sku = typeof productOrSku === "string" ? productOrSku : productOrSku?.sku;
  const match = String(sku || "").trim().toUpperCase().match(/^(LS-\d{3}[A-Z])/);

  return match ? match[1] : String(sku || "").trim().toUpperCase();
}

export function filterLandstarProductsForSelection(driverProfile, products) {
  const selectionRule = getLandstarSelectionRule(driverProfile);
  const allowedSkuFamilies = selectionRule.allowedSkuFamilies || [];
  const excludedSkuFamilies = selectionRule.excludedSkuFamilies || [];

  const filteredProducts = (Array.isArray(products) ? products : []).filter((product) => {
    const familySku = getLandstarProductFamilySku(product);

    if (excludedSkuFamilies.includes(familySku)) {
      return false;
    }

    return !allowedSkuFamilies.length || allowedSkuFamilies.includes(familySku);
  });

  if (selectionRule.mode !== "required_kit") {
    return filteredProducts;
  }

  return (selectionRule.requiredSkuFamilies || [])
    .map((requiredSkuFamily) =>
      filteredProducts.find(
        (product) => getLandstarProductFamilySku(product) === requiredSkuFamily
      )
    )
    .filter(Boolean);
}

export function getLandstarRequiredProductIds(driverProfile, products) {
  const requiredSkuFamilies =
    getLandstarSelectionRule(driverProfile).requiredSkuFamilies || [];

  if (!requiredSkuFamilies.length) {
    return [];
  }

  return requiredSkuFamilies
    .map((requiredSkuFamily) =>
      (Array.isArray(products) ? products : []).find(
        (product) => getLandstarProductFamilySku(product) === requiredSkuFamily
      )
    )
    .filter(Boolean)
    .map((product) => String(product.id));
}

export function isLandstarSelectionValid(driverProfile, selectedProducts) {
  const selectionRule = getLandstarSelectionRule(driverProfile);
  const products = Array.isArray(selectedProducts) ? selectedProducts : [];
  const selectedSkuFamilies = products.map(getLandstarProductFamilySku);

  if (selectionRule.mode === "choose_one") {
    const allowedSkuFamilies = selectionRule.allowedSkuFamilies || [];

    return (
      products.length === 1 &&
      (!allowedSkuFamilies.length || allowedSkuFamilies.includes(selectedSkuFamilies[0]))
    );
  }

  if (selectionRule.mode === "required_kit") {
    const requiredSkuFamilies = selectionRule.requiredSkuFamilies || [];

    return (
      products.length === requiredSkuFamilies.length &&
      new Set(selectedSkuFamilies).size === selectedSkuFamilies.length &&
      requiredSkuFamilies.every((familySku) => selectedSkuFamilies.includes(familySku))
    );
  }

  return products.length > 0;
}

export function getLandstarSelectionValidationMessage(driverProfile) {
  const selectionRule = getLandstarSelectionRule(driverProfile);

  return selectionRule.message || "Select at least one reward before continuing.";
}

export function isLandstarPersonalizationEnabled(driverProfile) {
  const normalizedYears = normalizeLandstarQualifiedYears(
    driverProfile?.qualifiedYears || driverProfile?.tierYear
  );

  return LANDSTAR_PERSONALIZATION_YEARS.includes(normalizedYears);
}

export function getDefaultProductSelection(product, selection = {}) {
  const defaultSize =
    selection.size || (Array.isArray(product?.sizes) && product.sizes.length === 1 ? product.sizes[0] : "");
  const defaultColor =
    selection.color ||
    (Array.isArray(product?.colors) && product.colors.length === 1 ? product.colors[0] : "");

  return {
    size: defaultSize,
    color: defaultColor,
    personalization: selection.personalization || ""
  };
}

export function buildSelectedProduct(product, selection) {
  const normalizedSelection = getDefaultProductSelection(product, selection);
  const selectedVariant = resolveLandstarProductVariant(product, normalizedSelection);

  return {
    ...product,
    sku: selectedVariant?.sku || product.sku,
    image: selectedVariant?.image || product.image,
    personalizationOptionId:
      selectedVariant?.personalizationOptionId || product.personalizationOptionId,
    selectedSize: normalizedSelection.size,
    selectedColor: normalizedSelection.color,
    personalization: normalizedSelection.personalization
  };
}

export function buildSelectedProducts(products, productSelections, selectedProductIds) {
  return selectedProductIds
    .map((productId) => {
      const product = products.find((entry) => String(entry.id) === String(productId));

      if (!product) {
        return null;
      }

      return buildSelectedProduct(product, productSelections[product.id] || {});
    })
    .filter(Boolean);
}

export function canSubmitRedemption({ driverProfile, selectedProducts, form }) {
  const hasValidSelections =
    Array.isArray(selectedProducts) &&
    isLandstarSelectionValid(driverProfile, selectedProducts) &&
    selectedProducts.every((product) => {
      const normalizedSelection = getDefaultProductSelection(product, {
        size: product.selectedSize,
        color: product.selectedColor,
        personalization: product.personalization,
      });

      return Boolean(normalizedSelection.size) && Boolean(normalizedSelection.color);
    });

  return (
    Boolean(driverProfile) &&
    hasValidSelections &&
    Boolean(form.firstName) &&
    Boolean(form.lastName) &&
    Boolean(form.email) &&
    isValidLandstarPhone(form.phone) &&
    Boolean(form.address1) &&
    Boolean(form.city) &&
    Boolean(form.state) &&
    Boolean(form.zip) &&
    Boolean(form.driverCode)
  );
}

export function getDisplayProductDescription(value) {
  if (!value) {
    return "";
  }

  return decodeHtmlEntities(
    decodeHtmlEntities(value)
      .replace(/<[^>]+>/g, " ")
  )
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeLandstarProductImageUrl(value) {
  if (!value) {
    return "";
  }

  if (!value.includes("/rest/")) {
    return value;
  }

  return value.replace(/\/rest\/[^/]+\/V1\/media\/catalog\/product/i, "/media/catalog/product");
}

function resolveLandstarProductVariant(product, selection) {
  if (!Array.isArray(product?.variants) || !product.variants.length) {
    return null;
  }

  return (
    product.variants.find(
      (variant) =>
        variant.size === selection.size &&
        variant.color === selection.color
    ) ||
    product.variants.find((variant) => variant.size === selection.size) ||
    product.variants.find((variant) => variant.color === selection.color) ||
    null
  );
}

function decodeHtmlEntities(value) {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

function normalizeLandstarQualifiedYears(value) {
  const parsedValue = String(value || "").match(/\d+/);
  return parsedValue ? parsedValue[0] : "";
}
